import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartAccount, LocaleCode, OperationLine } from '@/api/models';
import { findLedgerAccount } from '@/pages/accounting/books/chartOfAccounts/utils';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type {
    DiffLine,
    PairDiff,
    VersionDiff as Diff,
} from '../../operationDiff';

type VersionDiffProps = {
    diff: Diff | null;
    accounts: ChartAccount[];
    locale: LocaleCode;
    /** Version number, for the testid discriminator. */
    version: number;
};

/**
 * What one version changed about the recipe.
 *
 * **Unchanged pairs collapse to a single line.** A version that swapped one
 * account in a four-pair recipe should read as one change, not as four pairs of
 * which three happen to be identical — the reader is looking for the difference
 * and the rest is context they already have.
 *
 * **A changed line is rendered old → new**, struck through and then highlighted,
 * so the change is legible without a second column to scan across. What changed
 * is named beside it — account, amount, label, filter — because "5300 → 5400" and
 * "GROSS → NET" look alike at a glance and mean very different things.
 *
 * `diff` is `null` on the **first** version, which has nothing to be compared
 * against. That renders as "the original recipe", not as a change to one.
 */
export const VersionDiff: React.FC<VersionDiffProps> = ({
    diff,
    accounts,
    locale,
    version,
}) => {
    const { t } = useTranslation('pages');

    if (diff === null) {
        return (
            <p
                className="text-sm text-muted"
                {...getNodeTestIdProp(
                    `operations-history-diff-first-${version}`,
                )}
            >
                {t('operations.history.diff.first')}
            </p>
        );
    }

    if (diff.isIdentical) {
        return (
            <p
                className="text-sm text-muted"
                {...getNodeTestIdProp(
                    `operations-history-diff-identical-${version}`,
                )}
            >
                {t('operations.history.diff.identical')}
            </p>
        );
    }

    return (
        <ul
            className="flex flex-col gap-2"
            {...getNodeTestIdProp(`operations-history-diff-${version}`)}
        >
            {diff.pairs.map((pair) => (
                <PairRow
                    accounts={accounts}
                    key={pair.key}
                    locale={locale}
                    pair={pair}
                    version={version}
                />
            ))}
        </ul>
    );
};

type PairRowProps = {
    pair: PairDiff;
    accounts: ChartAccount[];
    locale: LocaleCode;
    version: number;
};

const PairRow: React.FC<PairRowProps> = ({
    pair,
    accounts,
    locale,
    version,
}) => {
    const { t } = useTranslation('pages');

    if (pair.status === 'UNCHANGED') {
        return (
            <li
                className="text-xs text-muted"
                {...getNodeTestIdProp(
                    `operations-history-pair-unchanged-${version}-${pair.key}`,
                )}
            >
                {t('operations.history.diff.pairUnchanged', {
                    number: pair.pairGroup,
                })}
            </li>
        );
    }

    return (
        <li
            className="flex flex-col gap-1 rounded-xl border border-separator p-3"
            {...getNodeTestIdProp(
                `operations-history-pair-${version}-${pair.key}`,
            )}
        >
            <span className="text-xs font-medium uppercase tracking-wide text-muted">
                {t(`operations.history.diff.pair.${pair.status}`, {
                    number: pair.pairGroup,
                })}
            </span>
            {pair.lines.map((line) => (
                <LineRow
                    accounts={accounts}
                    key={line.side}
                    line={line}
                    locale={locale}
                />
            ))}
        </li>
    );
};

/**
 * A line's account, in the words a reader recognises: `5300 — Rent expense` for
 * a fixed line, the slot's own question for a slot.
 *
 * An id the chart cannot resolve renders as a marker rather than as the uuid —
 * a bare id in a diff is noise, and the case it covers is an account archived
 * out of the chart since, which is worth saying rather than showing.
 */
const describe = (
    line: OperationLine | null,
    accounts: ChartAccount[],
    locale: LocaleCode,
    unknown: string,
    emptyLabel: string,
): string => {
    if (!line) {
        return emptyLabel;
    }
    if (line.accountId === null) {
        return (
            (locale === 'ka' ? line.slotLabelKa : line.slotLabelEn) ||
            line.slotLabelEn ||
            line.slotLabelKa ||
            unknown
        );
    }
    const account = findLedgerAccount(accounts, line.accountId);
    return account ? `${account.code} — ${account.name}` : unknown;
};

type LineRowProps = {
    line: DiffLine;
    accounts: ChartAccount[];
    locale: LocaleCode;
};

const LineRow: React.FC<LineRowProps> = ({ line, accounts, locale }) => {
    const { t } = useTranslation('pages');
    const unknown = t('operations.history.diff.unknownAccount');
    const emptyLabel = t('operations.history.diff.noLine');
    const isChanged = line.changes.length > 0;

    const before = describe(line.before, accounts, locale, unknown, emptyLabel);
    const after = describe(line.after, accounts, locale, unknown, emptyLabel);

    return (
        <span className="flex flex-wrap items-baseline gap-2 text-sm">
            <span className="w-14 shrink-0 text-xs uppercase tracking-wide text-muted">
                {t(
                    line.side === 'DEBIT'
                        ? 'operations.history.diff.debit'
                        : 'operations.history.diff.credit',
                )}
            </span>
            {isChanged && line.before ? (
                <span className="text-muted line-through">{before}</span>
            ) : null}
            {line.after ? (
                <span
                    className={cn(
                        isChanged
                            ? 'rounded bg-success-soft px-1.5 text-success-soft-foreground'
                            : 'text-foreground',
                    )}
                >
                    {after}
                </span>
            ) : (
                <span className="text-muted line-through">{before}</span>
            )}
            {isChanged ? (
                <span className="text-xs text-muted">
                    {line.changes
                        .map((change) =>
                            t(`operations.history.diff.changed.${change}`),
                        )
                        .join(', ')}
                </span>
            ) : null}
            {/* The amount source is part of what a line *is*, and a pair that
                changed its source shows the same account twice otherwise — so
                it rides along on every rendered line rather than only on the
                ones whose source moved. */}
            {line.after ? (
                <span className="text-xs text-muted">
                    {t(
                        `operations.history.amountSource.${line.after.amountSource}`,
                    )}
                </span>
            ) : null}
        </span>
    );
};
